import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  /** Short name of the action, shown in bold. */
  label: string
  /** Optional one-line explanation under the label. */
  hint?: string
  children: ReactNode
}

const GAP = 8
const EDGE = 8

/**
 * Names an icon-only control on hover and on keyboard focus.
 *
 * The native title attribute appears after about a second and not at all on
 * focus, so people read the icons as unlabeled. This shows at once.
 *
 * Rendered into document.body with fixed positioning, because the tables these
 * icons sit in scroll horizontally and would clip anything positioned inside.
 */
export default function Tooltip({ label, hint, children }: TooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null)

  const show = () => {
    if (anchorRef.current) setAnchor(anchorRef.current.getBoundingClientRect())
  }
  const hide = () => {
    setAnchor(null)
    setPos(null)
  }

  // Placed above the icon, or below when there is no room, and kept inside
  // the viewport sideways. Measured after render, since the width depends on
  // the translated text.
  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) return
    const tip = tipRef.current.getBoundingClientRect()
    const centre = anchor.left + anchor.width / 2
    const left = Math.min(Math.max(centre - tip.width / 2, EDGE), window.innerWidth - tip.width - EDGE)
    const below = anchor.top - tip.height - GAP < EDGE
    const top = below ? anchor.bottom + GAP : anchor.top - tip.height - GAP
    setPos({ left, top, below })
  }, [anchor, label, hint])

  // A fixed tooltip would stay put while the page moves under it.
  useLayoutEffect(() => {
    if (!anchor) return
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [anchor])

  return (
    <span
      ref={anchorRef}
      className="inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {anchor &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="z-[100] max-w-xs px-2.5 py-1.5 rounded-md bg-gray-900 text-white shadow-lg pointer-events-none"
          >
            <div className="text-xs font-semibold whitespace-nowrap">{label}</div>
            {hint && <div className="mt-0.5 text-xs text-gray-300">{hint}</div>}
          </div>,
          document.body
        )}
    </span>
  )
}
